import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  Group as GroupIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import authService from '../../../services/auth/authService';
import api from '../../../services/api';

/**
 * Recipient Groups tab for the Daily Email Flow page.
 *
 * Manages the TO/CC user groups (public.user_groups) that email workflows
 * reference as recipients. Groups created here populate the TO/CC dropdowns
 * in the Workflows tab. Backend: /api/groups.
 */
const RecipientGroupsTab = () => {
  const theme = useTheme();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [emailRole, setEmailRole] = useState('TO');
  // Pending "Assign User" selection per group card, keyed by group id: { [groupId]: userId }
  const [selectedUserByGroup, setSelectedUserByGroup] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  // Keep pending selections valid: drop any whose user is no longer assignable
  // (e.g., they were just assigned to another group and filtered out on refetch).
  useEffect(() => {
    setSelectedUserByGroup(prev => {
      const availableIds = new Set(
        users.filter(u => u.group_id === null || u.group_id === undefined).map(u => u.id)
      );
      let changed = false;
      const next = {};
      for (const [gid, uid] of Object.entries(prev)) {
        if (availableIds.has(uid)) next[gid] = uid;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [users]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [groupsRes, usersRes] = await Promise.all([
        api.get('/groups/details'),
        authService.getUsers(true),
      ]);
      setGroups(groupsRes.data);
      setUsers(usersRes.users);
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (group = null) => {
    setSelectedGroup(group);
    setGroupName(group ? group.name : '');
    setEmailRole(group ? group.email_role : 'TO');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedGroup(null);
    setGroupName('');
    setEmailRole('TO');
  };

  const handleSave = async () => {
    try {
      const payload = { name: groupName, email_role: emailRole };
      if (selectedGroup) {
        await api.put(`/groups/${selectedGroup.id}`, payload);
      } else {
        await api.post('/groups', payload);
      }
      fetchData();
      handleClose();
    } catch (err) {
      setError('Failed to save group');
    }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Delete this group? Users will be unassigned. Groups used by a workflow cannot be deleted until removed from that workflow.')) {
      return;
    }
    try {
      await api.delete(`/groups/${groupId}`);
      fetchData();
    } catch (err) {
      // to_group / cc group FKs are ON DELETE RESTRICT, so a group in use by a
      // workflow will reject deletion. Surface a friendly hint instead of a raw 500.
      setError('Failed to delete group. If this group is used by an email workflow, remove it from that workflow first.');
    }
  };

  const handleAssignUser = async (groupId) => {
    const userId = selectedUserByGroup[groupId];
    if (!userId) return;
    try {
      await api.post(`/groups/${groupId}/assign-user`, { userId });
      setSelectedUserByGroup(prev => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
      fetchData();
    } catch (err) {
      setError('Failed to assign user');
    }
  };

  const handleUnassignUser = async (userId) => {
    try {
      await api.post('/groups/unassign-user', { userId });
      fetchData();
    } catch (err) {
      setError('Failed to unassign user');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Action Button Section */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Recipient Groups
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Groups of users that receive the daily defect emails. TO = primary recipients, CC = awareness. Used by the workflows in the Workflows tab.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Create Group
          </Button>
        </Stack>
      </Paper>

      {/* Groups Grid - Using CSS Grid like AssetsPage */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)'
          },
          gap: 3,
          mb: 3
        }}
      >
        {groups.map((group) => (
          <Card
            key={group.id}
            sx={{
              borderRadius: 3,
              boxShadow: theme.shadows[2],
              '&:hover': {
                boxShadow: theme.shadows[4],
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease-in-out',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
              <CardHeader
                avatar={
                  <Box sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: '50%',
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <GroupIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  </Box>
                }
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" fontWeight={600}>
                      {group.name}
                    </Typography>
                    <Chip
                      label={group.email_role}
                      size="small"
                      icon={<EmailIcon />}
                      color={group.email_role === 'TO' ? 'primary' : 'secondary'}
                      sx={{ fontWeight: 500 }}
                    />
                  </Stack>
                }
                subheader={
                  <Typography variant="body2" color="text.secondary">
                    {group.users.length} user{group.users.length !== 1 ? 's' : ''} assigned
                  </Typography>
                }
                action={
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit Group">
                      <IconButton
                        size="small"
                        onClick={() => handleOpen(group)}
                        sx={{ color: 'primary.main' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Group">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(group.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
                sx={{ pb: 1 }}
              />

              <CardContent sx={{ pt: 0, flexGrow: 1 }}>
                {/* Add User Section */}
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ flexGrow: 1 }}>
                      <InputLabel>Assign User</InputLabel>
                      <Select
                        value={selectedUserByGroup[group.id] || ''}
                        onChange={(e) =>
                          setSelectedUserByGroup(prev => ({ ...prev, [group.id]: e.target.value }))
                        }
                        label="Assign User"
                      >
                        {users.filter(u => u.group_id === null || u.group_id === undefined).map(user => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.full_name || user.username}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PersonAddIcon />}
                      onClick={() => handleAssignUser(group.id)}
                      disabled={!selectedUserByGroup[group.id]}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Assign
                    </Button>
                  </Stack>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Users Table */}
                {group.users.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                          <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Disciplines</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Location Permissions</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.users.map((user) => (
                          <TableRow
                            key={user.id}
                            sx={{
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.02)
                              }
                            }}
                          >
                            <TableCell sx={{ maxWidth: '150px' }}>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={user.full_name || user.username}
                              >
                                {user.full_name || user.username}
                              </Typography>
                              {user.email && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={user.email}
                                >
                                  {user.email}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {user.disciplines && user.disciplines.length > 0 ? (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Chip
                                    label={user.disciplines[0]}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                  {user.disciplines.length > 1 && (
                                    <Tooltip
                                      title={
                                        <Box>
                                          <Typography variant="caption" fontWeight={600} display="block">
                                            All Disciplines:
                                          </Typography>
                                          {user.disciplines.map((discipline, index) => (
                                            <Typography key={index} variant="caption" display="block">
                                              • {discipline}
                                            </Typography>
                                          ))}
                                        </Box>
                                      }
                                      arrow
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          cursor: 'pointer',
                                          '&:hover': {
                                            color: 'primary.main',
                                            textDecoration: 'underline'
                                          }
                                        }}
                                      >
                                        +{user.disciplines.length - 1} more
                                      </Typography>
                                    </Tooltip>
                                  )}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No disciplines
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ maxWidth: '200px' }}>
                              {user.permission_scope === 'organization' ? (
                                <Chip
                                  label="Organization Wide"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              ) : user.permission_scope === 'network_section' && user.permissions && user.permissions.length > 0 ? (
                                <Tooltip
                                  title={
                                    <Box>
                                      <Typography variant="caption" fontWeight={600} display="block">
                                        All Location Permissions:
                                      </Typography>
                                      {user.permissions.map((permission, index) => (
                                        <Typography key={index} variant="caption" display="block">
                                          • {permission}
                                        </Typography>
                                      ))}
                                    </Box>
                                  }
                                  arrow
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        color: 'primary.main'
                                      }
                                    }}
                                  >
                                    {user.permissions.join(', ')}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No permissions
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Remove User">
                                <IconButton
                                  size="small"
                                  onClick={() => handleUnassignUser(user.id)}
                                  sx={{ color: 'error.main' }}
                                >
                                  <PersonRemoveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      backgroundColor: alpha(theme.palette.grey[100], 0.5),
                      borderRadius: 2,
                      border: `1px dashed ${alpha(theme.palette.grey[400], 0.5)}`
                    }}
                  >
                    <GroupIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No users assigned to this group
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      Use the form above to assign users
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
        ))}
      </Box>

      {groups.length === 0 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              backgroundColor: alpha(theme.palette.grey[100], 0.3),
              borderRadius: 2,
              border: `2px dashed ${alpha(theme.palette.grey[400], 0.5)}`
            }}
          >
            <GroupIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No groups created yet
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              Create your first group to start organizing recipients
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
            >
              Create First Group
            </Button>
          </Box>
        </Paper>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[10]
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          pb: 2
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <GroupIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {selectedGroup ? 'Edit Group' : 'Create Group'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <TextField
              autoFocus
              label="Group Name"
              type="text"
              fullWidth
              variant="outlined"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name..."
              sx={{ mt: 1 }}
            />
            <FormControl fullWidth variant="outlined">
              <InputLabel>Email Role</InputLabel>
              <Select
                value={emailRole}
                onChange={(e) => setEmailRole(e.target.value)}
                label="Email Role"
              >
                <MenuItem value="TO">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EmailIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>TO</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Primary recipients
                      </Typography>
                    </Box>
                  </Stack>
                </MenuItem>
                <MenuItem value="CC">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EmailIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>CC</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Carbon copy recipients
                      </Typography>
                    </Box>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          pt: 2,
          px: 3,
          pb: 3
        }}>
          <Button onClick={handleClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!groupName.trim()}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {selectedGroup ? 'Update Group' : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecipientGroupsTab;
